import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  listCarriers, createCarrier, toggleCarrier,
  createCarrierService, deleteCarrierService, listCustomers,
} from "@/lib/admin-config.functions";
import {
  syncCarrierCatalog, toggleCarrierServiceActive,
} from "@/lib/carrier-catalog.functions";
import { runServerFn } from "@/lib/server-fn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Loader2, Truck, Trash2, ArrowLeft, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { CarrierIntegrationsPanel } from "@/components/admin/carrier-integrations-panel";

export const Route = createFileRoute(
  "/_authenticated/app/admin/customers/$customerId/carriers",
)({ component: CustomerCarriersPage });

function CustomerCarriersPage() {
  const { customerId } = Route.useParams();
  const qc = useQueryClient();

  const fetchCustomers = useServerFn(listCustomers);
  const fetchCarriers = useServerFn(listCarriers);
  const createFn = useServerFn(createCarrier);
  const toggleFn = useServerFn(toggleCarrier);
  const createSvcFn = useServerFn(createCarrierService);
  const deleteSvcFn = useServerFn(deleteCarrierService);
  const syncCatalogFn = useServerFn(syncCarrierCatalog);
  const toggleSvcActiveFn = useServerFn(toggleCarrierServiceActive);

  const { data: customers } = useQuery({
    queryKey: ["customers"],
    queryFn: () => runServerFn(() => fetchCustomers(), "Loading customers"),
  });
  const customer = (customers ?? []).find((c: any) => c.id === customerId);
  const tenantId = customer?.tenant_id as string | undefined;

  const { data, isLoading } = useQuery({
    queryKey: ["carriers", tenantId, customerId],
    enabled: !!tenantId,
    queryFn: () =>
      runServerFn(
        () => fetchCarriers({ data: { tenantId: tenantId!, customerId } }),
        "Loading carriers",
      ),
  });
  const carriers = data?.carriers ?? [];
  const services = data?.services ?? [];

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ["carriers", tenantId, customerId] });

  const [open, setOpen] = useState(false);
  const [code, setCode] = useState(""); const [name, setName] = useState(""); const [trackUrl, setTrackUrl] = useState("");
  const [shipFromKey, setShipFromKey] = useState<string>("none");
  const [submitting, setSubmitting] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const [svcOpen, setSvcOpen] = useState(false);
  const [svcCarrierId, setSvcCarrierId] = useState<string>("");
  const [svcCode, setSvcCode] = useState(""); const [svcName, setSvcName] = useState(""); const [svcDays, setSvcDays] = useState("");

  const customerAddresses = ((customer?.contact_info as any)?.addresses ?? {}) as Record<string, unknown>;
  const ADDR_OPTS = [
    { key: "ship_from", label: "Ship From" },
    { key: "return_to", label: "Return To" },
    { key: "business_physical", label: "Business Physical Address" },
  ] as const;
  const availableAddrs = ADDR_OPTS.filter((o) => customerAddresses[o.key]);

  const addCarrier = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenantId || !code.trim() || !name.trim()) return;
    setSubmitting(true);
    try {
      await runServerFn(
        () => createFn({ data: {
          tenantId, customerId,
          code: code.trim(), name: name.trim(),
          trackingUrlTemplate: trackUrl.trim() || null,
          shipFromAddressKey: shipFromKey === "none" ? null : shipFromKey as any,
        } }),
        "Adding carrier",
      );
      toast.success("Carrier added");
      setOpen(false); setCode(""); setName(""); setTrackUrl(""); setShipFromKey("none");
      invalidate();
    } catch { /* */ } finally { setSubmitting(false); }
  };

  const addService = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!svcCarrierId || !svcCode.trim() || !svcName.trim()) return;
    const days = svcDays ? Number(svcDays) : null;
    try {
      await runServerFn(() => createSvcFn({ data: {
        carrierId: svcCarrierId, code: svcCode.trim(), name: svcName.trim(),
        transitDays: Number.isFinite(days as number) ? days : null,
      } }), "Adding service");
      toast.success("Service added");
      setSvcOpen(false); setSvcCode(""); setSvcName(""); setSvcDays("");
      invalidate();
    } catch { /* */ }
  };

  const toggle = async (id: string, active: boolean) => {
    try { await runServerFn(() => toggleFn({ data: { id, active: !active } }), "Updating carrier"); invalidate(); } catch { /* */ }
  };
  const removeService = async (id: string) => {
    try { await runServerFn(() => deleteSvcFn({ data: { id } }), "Removing service"); invalidate(); } catch { /* */ }
  };
  const toggleServiceActive = async (id: string, active: boolean) => {
    try { await runServerFn(() => toggleSvcActiveFn({ data: { id, active } }), "Updating service"); invalidate(); } catch { /* */ }
  };
  const syncCatalog = async (carrierId: string, carrierName: string) => {
    try {
      const res = await runServerFn(() => syncCatalogFn({ data: { carrierId } }), `Syncing ${carrierName} catalog`);
      if (res && typeof res.inserted === "number") {
        toast.success(res.inserted > 0
          ? `${res.inserted} service${res.inserted === 1 ? "" : "s"} added from provider catalog`
          : `${carrierName} catalog already in sync`);
      }
      invalidate();
    } catch { /* */ }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <Button asChild variant="ghost" size="sm" className="mb-1 -ml-2">
            <Link to="/app/admin/customers"><ArrowLeft className="h-3 w-3 mr-1" />Customers</Link>
          </Button>
          <h2 className="text-lg font-semibold">Carriers — {customer?.name ?? "…"}</h2>
          <p className="text-sm text-muted-foreground">Customer-owned carriers plus tenant-wide carriers (read-only here).</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button disabled={!tenantId}><Plus className="h-4 w-4 mr-2" />New carrier</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add carrier for {customer?.name}</DialogTitle></DialogHeader>
            <form onSubmit={addCarrier} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Code</Label><Input required value={code} onChange={(e) => setCode(e.target.value)} placeholder="UPS" /></div>
                <div className="space-y-2"><Label>Name</Label><Input required value={name} onChange={(e) => setName(e.target.value)} placeholder="United Parcel Service" /></div>
              </div>
              <div className="space-y-2">
                <Label>Tracking URL template</Label>
                <Input value={trackUrl} onChange={(e) => setTrackUrl(e.target.value)} placeholder="https://www.ups.com/track?tracknum={tracking}" />
              </div>
              <div className="space-y-2">
                <Label>Ship From address</Label>
                <Select value={shipFromKey} onValueChange={setShipFromKey}>
                  <SelectTrigger>
                    <SelectValue placeholder="Use customer default" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Use customer default</SelectItem>
                    {availableAddrs.length === 0 ? (
                      <SelectItem value="__none" disabled>No customer addresses on file</SelectItem>
                    ) : availableAddrs.map((o) => (
                      <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {availableAddrs.length === 0 && (
                  <p className="text-xs text-muted-foreground">Add addresses on the customer's edit form to pick one here.</p>
                )}
              </div>
              <DialogFooter><Button type="submit" disabled={submitting}>{submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Add</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="border rounded-lg bg-card overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Scope</TableHead>
            <TableHead>Services</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
            ) : carriers.length > 0 ? carriers.map((c: any) => {
              const svcs = services.filter((s: any) => s.carrier_id === c.id);
              const isOpen = expanded === c.id;
              const tenantWide = !c.customer_id;
              return (
                <>
                  <TableRow key={c.id} className="cursor-pointer" onClick={() => setExpanded(isOpen ? null : c.id)}>
                    <TableCell className="font-mono text-sm flex items-center gap-2"><Truck className="h-4 w-4 text-muted-foreground" />{c.code}</TableCell>
                    <TableCell>{c.name}</TableCell>
                    <TableCell>
                      {tenantWide ? <Badge variant="secondary">Tenant-wide</Badge> : <Badge>Customer</Badge>}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">{svcs.length}</TableCell>
                    <TableCell className="text-muted-foreground">{c.active ? "active" : "archived"}</TableCell>
                    <TableCell className="text-right">
                      {!tenantWide && (
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); toggle(c.id, c.active); }}>
                          {c.active ? "Archive" : "Restore"}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {isOpen && (
                    <TableRow key={`${c.id}-svc`}>
                      <TableCell colSpan={6} className="bg-muted/30">
                        <div className="p-3 space-y-3">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground uppercase">Service types</span>
                            {!tenantWide && (
                              <div className="flex items-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => syncCatalog(c.id, c.name)}
                                  title="Import every provider-native service code from the carrier's catalog. Existing rows are left untouched."
                                >
                                  <RefreshCw className="h-3 w-3 mr-1" />Sync from provider
                                </Button>
                              <Dialog open={svcOpen && svcCarrierId === c.id} onOpenChange={(o) => { setSvcOpen(o); if (o) setSvcCarrierId(c.id); }}>
                                <DialogTrigger asChild><Button variant="outline" size="sm"><Plus className="h-3 w-3 mr-1" />Add service</Button></DialogTrigger>
                                <DialogContent>
                                  <DialogHeader><DialogTitle>Add service to {c.name}</DialogTitle></DialogHeader>
                                  <form onSubmit={addService} className="space-y-4">
                                    <div className="grid grid-cols-2 gap-3">
                                      <div className="space-y-2"><Label>Code</Label><Input required value={svcCode} onChange={(e) => setSvcCode(e.target.value)} placeholder="GROUND" /></div>
                                      <div className="space-y-2"><Label>Transit days</Label><Input type="number" min="0" value={svcDays} onChange={(e) => setSvcDays(e.target.value)} /></div>
                                    </div>
                                    <div className="space-y-2"><Label>Name</Label><Input required value={svcName} onChange={(e) => setSvcName(e.target.value)} placeholder="UPS Ground" /></div>
                                    <DialogFooter><Button type="submit">Add</Button></DialogFooter>
                                  </form>
                                </DialogContent>
                              </Dialog>
                              </div>
                            )}
                          </div>
                          {svcs.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              No services yet. Use “Sync from provider” to pull the full carrier catalog, then toggle the ones you want to offer operators.
                            </p>
                          ) : (
                            <div className="divide-y border rounded-md bg-card">
                              {svcs.map((s: any) => (
                                <div key={s.id} className="flex items-center justify-between px-3 py-2">
                                  <div className="flex items-center gap-3">
                                    <span className="font-mono text-xs">{s.code}</span>
                                    <span className="text-sm">{s.name}</span>
                                    {s.transit_days != null && <span className="text-xs text-muted-foreground">{s.transit_days}d</span>}
                                    {!s.active && <Badge variant="secondary" className="text-[10px]">disabled</Badge>}
                                  </div>
                                  {!tenantWide && (
                                    <div className="flex items-center gap-3">
                                      <div className="flex items-center gap-2">
                                        <span className="text-[11px] text-muted-foreground">
                                          {s.active ? "Offered" : "Hidden"}
                                        </span>
                                        <Switch
                                          checked={!!s.active}
                                          onCheckedChange={(next) => toggleServiceActive(s.id, next)}
                                          aria-label={`${s.active ? "Disable" : "Enable"} ${s.name}`}
                                        />
                                      </div>
                                      <Button variant="ghost" size="icon" onClick={() => removeService(s.id)} title="Remove service">
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </>
              );
            }) : (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No carriers yet.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="pt-2">
        <CarrierIntegrationsPanel tenantId={tenantId} customerId={customerId} />
      </div>
    </div>
  );
}